import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  Badge,
  Input,
  Select
} from '../../styles/StyledComponents';
import IconWrapper from '../common/IconWrapper';
import { 
  FaBell, 
  FaCheck, 
  FaTimes, 
  FaEye, 
  FaDownload,
  FaFilePdf,
  FaFilter,
  FaSearch
} from 'react-icons/fa';
import apiService from '../../services/api';
import { Notification } from '../../types';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  h1 {
    color: var(--secondary-color);
    font-size: 1.8rem;
    margin: 0;
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.3rem;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background-color: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  label {
    font-weight: 500;
    color: var(--dark-gray);
    white-space: nowrap;
    font-size: 0.9rem;
    
    @media (max-width: 768px) {
      font-size: 0.85rem;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 0.75rem;
  }
`;

const NotificationCard = styled(Card)`
  margin-bottom: 1rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-left: 4px solid var(--primary-color);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
  
  &.unread {
    background-color: rgba(246, 139, 33, 0.05);
    border-left-color: var(--primary-color);
  }
  
  .notification-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    gap: 1rem;
    
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }
  
  .notification-title {
    font-weight: 600;
    color: var(--secondary-color);
    font-size: 1.1rem;
    margin-bottom: 0.25rem;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
  
  .notification-type {
    font-size: 0.85rem;
    color: var(--dark-gray);
    background: var(--light-gray);
    padding: 0.25rem 0.5rem;
    border-radius: 6px;
    display: inline-block;
    
    @media (max-width: 768px) {
      font-size: 0.8rem;
    }
  }
  
  .notification-message {
    color: var(--dark-gray);
    font-size: 0.95rem;
    line-height: 1.5;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }
  
  .notification-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }
  
  .notification-time {
    font-size: 0.85rem;
    color: var(--medium-gray);
    
    @media (max-width: 768px) {
      font-size: 0.8rem;
    }
  }
  
  .notification-actions {
    display: flex;
    gap: 0.5rem;
    
    @media (max-width: 768px) {
      flex-wrap: wrap;
      gap: 0.75rem;
    }
  }
`;

const StatusBadge = styled(Badge)`
  min-width: 80px;
  text-align: center;
  font-size: 0.75rem;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    min-width: 70px;
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--dark-gray);
  
  .empty-icon {
    font-size: 4rem;
    color: var(--light-gray);
    margin-bottom: 1.5rem;
    
    @media (max-width: 768px) {
      font-size: 3rem;
    }
  }
  
  .empty-title {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
  .empty-description {
    font-size: 1rem;
    line-height: 1.6;
    max-width: 400px;
    margin: 0 auto;
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
      max-width: 300px;
    }
  }
`;

const SupervisorNotifications: React.FC = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filteredNotifications, setFilteredNotifications] = useState<Notification[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load notifications from API
  useEffect(() => {
    const loadNotifications = async () => {
      if (!user) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await apiService.getNotifications({
          userId: user.id,
          limit: 100
        });
        
        if (response.success && response.data) {
          setNotifications(response.data);
          setFilteredNotifications(response.data);
        } else {
          setNotifications([]);
          setFilteredNotifications([]);
        }
      } catch (error: any) {
        console.error('Error loading notifications:', error);
        setError('Failed to load notifications');
        setNotifications([]);
        setFilteredNotifications([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadNotifications();
  }, [user]);

  useEffect(() => {
    let filtered = notifications;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(notification =>
        notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notification.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notification.type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(notification => notification.type === typeFilter);
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(notification => 
        statusFilter === 'unread' ? !notification.read : notification.read
      );
    }

    setFilteredNotifications(filtered);
  }, [searchTerm, typeFilter, statusFilter, notifications]);

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - dateObj.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    }
  };

  const getPriorityVariant = (type: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' => {
    switch (type) {
      case 'Approval Required': return 'danger';
      case 'Rejected': return 'danger';
      case 'Approved': return 'success';
      case 'Assignment': return 'warning';
      case 'System Alert': return 'info';
      default: return 'primary';
    }
  };

  const getTypeVariant = (type: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' => {
    switch (type) {
      case 'Approval Required': return 'danger';
      case 'Project Update': return 'info';
      case 'System Alert': return 'warning';
      case 'Assignment': return 'success';
      default: return 'primary';
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const response = await apiService.markNotificationAsRead(notificationId);
      
      if (response.success && response.data) {
        // Update the specific notification with the response data
        setNotifications(prev => 
          prev.map(notification => 
            notification.id === notificationId 
              ? response.data
              : notification
          )
        );
        
        // Also update filtered notifications
        setFilteredNotifications(prev => 
          prev.map(notification => 
            notification.id === notificationId 
              ? response.data
              : notification
          )
        );
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      if (!user) return;
      
      const response = await apiService.markAllNotificationsAsRead();
      
      if (response.success) {
        // Reload notifications to get the updated state
        const notificationsResponse = await apiService.getNotifications({
          userId: user.id,
          limit: 100
        });
        
        if (notificationsResponse.success && notificationsResponse.data) {
          setNotifications(notificationsResponse.data);
          setFilteredNotifications(notificationsResponse.data);
        }
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <PageContainer>
      <PageHeader>
        <h1>Notifications {unreadCount > 0 && `(${unreadCount} unread)`}</h1>
        <ActionButtons>
          <Button onClick={markAllAsRead} disabled={unreadCount === 0}>
            <IconWrapper icon={FaCheck} />
            Mark All as Read
          </Button>
          <Button>
            <IconWrapper icon={FaDownload} />
            Export
          </Button>
          <Button>
            <IconWrapper icon={FaFilePdf} />
            Print
          </Button>
        </ActionButtons>
      </PageHeader>

      <FilterSection>
        <SearchContainer>
          <Input
            type="text"
            placeholder="Search notifications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ paddingLeft: '2.5rem' }}
          />
          <IconWrapper 
            icon={FaSearch} 
            style={{ 
              position: 'absolute', 
              left: '0.75rem', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: 'var(--medium-gray)'
            }} 
          />
        </SearchContainer>

        <FilterGroup>
          <label>Type:</label>
          <Select 
            value={typeFilter} 
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{ minWidth: '120px' }}
          >
            <option value="all">All Types</option>
            <option value="Approval Required">Approval Required</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Assignment">Assignment</option>
            <option value="System Alert">System Alert</option>
            <option value="Disbursement">Disbursement</option>
            <option value="Deadline">Deadline</option>
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Status:</label>
          <Select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ minWidth: '120px' }}
          >
            <option value="all">All Status</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </Select>
        </FilterGroup>
      </FilterSection>

      {isLoading ? (
        <NoDataMessage>
          <div className="empty-icon">⏳</div>
          <div className="empty-title">Loading Notifications</div>
          <div className="empty-description">
            Please wait while we load your notifications...
          </div>
        </NoDataMessage>
      ) : error ? (
        <NoDataMessage>
          <div className="empty-icon">⚠️</div>
          <div className="empty-title">Error Loading Notifications</div>
          <div className="empty-description">
            {error}
          </div>
        </NoDataMessage>
      ) : filteredNotifications.length === 0 ? (
        <NoDataMessage>
          <div className="empty-icon">🔔</div>
          <div className="empty-title">No Notifications Found</div>
          <div className="empty-description">
            {searchTerm || typeFilter !== 'all' || statusFilter !== 'all'
              ? 'No notifications match your current filters. Try adjusting your search criteria.'
              : 'You have no notifications at the moment. New notifications will appear here.'
            }
          </div>
        </NoDataMessage>
      ) : (
        filteredNotifications.map((notification) => (
          <NotificationCard key={notification.id} className={!notification.read ? 'unread' : ''}>
            <div className="notification-header">
              <div>
                <div className="notification-title">{notification.title}</div>
                <div className="notification-type">{notification.type}</div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <StatusBadge variant={getPriorityVariant(notification.type)}>
                  {notification.type}
                </StatusBadge>
                <StatusBadge variant={notification.read ? 'success' : 'warning'}>
                  {notification.read ? 'Read' : 'Unread'}
                </StatusBadge>
              </div>
            </div>

            <div className="notification-message">
              {notification.message}
            </div>

            <div className="notification-footer">
              <div className="notification-time">
                {formatDate(notification.createdAt)}
              </div>
              <div className="notification-actions">
                {!notification.read && (
                  <Button 
                    onClick={() => markAsRead(notification.id)}
                    style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem' }}
                  >
                    <IconWrapper icon={FaCheck} />
                    Mark as Read
                  </Button>
                )}
                {notification.data?.actionUrl && (
                  <Button 
                    as="a" 
                    href={notification.data.actionUrl}
                    style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem' }}
                  >
                    <IconWrapper icon={FaEye} />
                    View Details
                  </Button>
                )}
                {/* Delete functionality removed - notifications are managed by the system */}
              </div>
            </div>
          </NotificationCard>
        ))
      )}
    </PageContainer>
  );
};

export default SupervisorNotifications; 